import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
//MUI
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

//Modal style
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  maxHeight: "85vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "1px solid #000",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

/** Quanto tempo a pessoa está à espera, em palavras. */
const espera = (desde) => {
  const minutos = Math.max(0, moment().diff(moment(desde), "minutes"));
  if (minutos < 1) return "agora";
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  return `${horas}h${String(minutos % 60).padStart(2, "0")}`;
};

/* Acima disto a linha fica a vermelho. É o mesmo patamar da primeira escalada
   em `jobs/jobUrgencias.mjs` — se divergirem, o sino avisa que ninguém atendeu
   enquanto a lista continua a mostrar a linha como normal. */
const MINUTOS_ATRASO = 15;

/**
 * A fila de quem está na sala de espera.
 *
 * Mostrava nome e hora de chegada. Faltava o essencial para decidir se se
 * interrompe o que se está a fazer: ao que a pessoa vem, o que o balcão
 * escreveu, e há quanto tempo espera.
 */
function UrgenciasModal({
  urgencias,
  handleClose,
  openModalUrgencia,
  atendidoConfirmation,
  /* Só um dentista assume uma urgência. O balcão vê a fila — é quem marcou e
     quem atende o telefone —, mas não pode dizer «vou eu»: esse clique tira a
     urgência da fila e cala a escalada, e se quem o faz não é quem vai à sala
     de espera, o paciente fica a esperar sem ninguém a saber.
     O servidor recusa de qualquer maneira (`exigirClinico`); isto é só para
     não mostrar um botão que ia dar erro. */
  podeAssumir = true,
  /* `aoDispensar(id, nome)` — tirar da fila sem atender. Opcional: a faixa do
     topo e as dashboards passam-no, e sem ele o botao nao aparece. */
  aoDispensar,
  /* "abertas" mostra so quem espera; "dia" mostra tudo o que se passou hoje,
     atendido ou dispensado. A faixa usa o primeiro, a dashboard o segundo. */
  escopo = "abertas",
}) {
  if (!urgencias) return null;

  const porEspera = (ur) => moment().diff(moment(ur.created_at), "minutes");

  /* O estado de cada linha. Uma urgencia atendida ou dispensada ja nao pede
     nada a ninguem — fica esbatida e sem botoes, mas fica. */
  const estadoDe = (ur) => {
    if (ur.atendido) return "atendida";
    /* O fecho do fim do dia marca `semResposta`. Nao e dispensada — ninguem a
       dispensou — e e o numero que o relatorio de urgencias conta. */
    if (ur.semResposta) return "sem-resposta";
    if (ur.ativo === false) return "dispensada";
    return "aberta";
  };

  return (
    <Modal
      open={openModalUrgencia}
      onClose={handleClose}
      aria-labelledby="modal-urgencias-titulo"
    >
      <Box sx={modalStyle}>
        <Typography
          id="modal-urgencias-titulo"
          variant="h6"
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          {escopo === "dia" ? "Urgências de hoje" : "Pacientes de Urgência"}
        </Typography>

        {urgencias.length === 0 && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
            {escopo === "dia"
              ? "Nenhuma urgência hoje."
              : "Ninguém à espera."}
          </Typography>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {urgencias.map((ur) => {
            const estado = estadoDe(ur);
            const aberta = estado === "aberta";
            /* O vermelho de atrasada so faz sentido em quem ainda espera. */
            const atrasada = aberta && porEspera(ur) >= MINUTOS_ATRASO;

            return (
              <Box
                key={ur.id || ur._id}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: atrasada ? "error.main" : "divider",
                  backgroundColor: atrasada ? "#fef2f2" : "transparent",
                  /* Esbatida, nao escondida: quem chega a meio da tarde quer
                     ver como foi a manha. */
                  opacity: aberta ? 1 : 0.6,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Link
                      to={`/clientes/${ur.cliente?.id || ur.cliente?._id}`}
                      className="link__dashboard"
                    >
                      <Typography
                        component="span"
                        sx={{ fontWeight: 600, color: "text.primary" }}
                      >
                        {ur.cliente?.nome || "(cliente apagado)"}
                      </Typography>
                    </Link>

                    {ur.motivo && (
                      <Chip
                        size="small"
                        color={aberta ? "error" : "default"}
                        label={ur.motivo}
                      />
                    )}

                    {/* Quem espera ve quanto tempo espera. Quem ja foi
                        atendido ve quanto tempo esperou — e nao um contador
                        que continua a subir para sempre. */}
                    {aberta && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={atrasada ? "error" : "default"}
                        label={`espera ${espera(ur.created_at)}`}
                      />
                    )}

                    {estado === "atendida" && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label="Atendida"
                      />
                    )}

                    {estado === "sem-resposta" && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "error.main" }}
                    >
                      O dia acabou e ninguem a atendeu nem dispensou.
                    </Typography>
                  )}

                  {estado === "dispensada" && (
                      <Chip size="small" variant="outlined" label="Dispensada" />
                    )}

                    {estado === "sem-resposta" && (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label="Sem resposta"
                      />
                    )}
                  </Box>

                  {ur.descricao && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {ur.descricao}
                    </Typography>
                  )}

                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Chegou às {moment(ur.created_at).format("HH:mm")}
                    {ur.cliente?.contacto && ` · ${ur.cliente.contacto}`}
                    {ur.criadoPor?.name && ` · marcada por ${ur.criadoPor.name}`}
                  </Typography>

                  {/* Quem resolveu, e quanto tempo o doente esperou. E a
                      pergunta que se faz ao olhar para o dia ja passado. */}
                  {estado === "atendida" && (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", color: "success.main" }}
                    >
                      Atendida por {ur.atendidoPor?.name || "—"}
                      {ur.atendidoEm &&
                        ` as ${moment(ur.atendidoEm).format("HH:mm")}`}
                      {ur.atendidoEm &&
                        ` · esperou ${moment(ur.atendidoEm).diff(
                          moment(ur.created_at),
                          "minutes",
                        )} min`}
                    </Typography>
                  )}

                  {estado === "dispensada" && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block" }}
                    >
                      Dispensada por {ur.canceladoPor?.name || "—"}
                      {ur.canceladoEm &&
                        ` as ${moment(ur.canceladoEm).format("HH:mm")}`}
                    </Typography>
                  )}

                  {/* ─── QUEM FOI AVISADO, PELO NOME ─────────────────────────
                      Estava «Avisados: 1 dentista(s)» com os nomes atrás de um
                      tooltip. Qualquer dentista pode assumir uma urgência — e
                      isso é de propósito —, mas sem ver A QUEM ela foi dirigida
                      vai atendê-la sem saber que outra pessoa já pode estar a
                      caminho. Os nomes ficam à vista. */}
                  {aberta && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      {ur.avisarMedicos?.length ? (
                        <>
                          Avisado(s):{" "}
                          <strong>
                            {ur.avisarMedicos.map((m) => m.name).join(", ")}
                          </strong>
                        </>
                      ) : (
                        "Avisados: todos os dentistas desta unidade"
                      )}
                    </Typography>
                  )}
                </Box>

                {/* «Vou atender agora» e não «Atendido»: no momento em que se
                    carrega, ainda não se atendeu ninguém — decidiu-se ir. É
                    este clique que tira a urgência da fila, da faixa e do sino
                    de todos, e que para a escalada.

                    Um segundo botão para «concluído» seria sempre esquecido, e
                    o que a clínica precisa de saber é se alguém está a tratar
                    da pessoa — o que foi feito fica no registo do dia. */}
                {/* Uma urgência já resolvida não tem botões: o que se podia
                    fazer está feito, e deixar lá o botão convidava a reabrir
                    o que ninguém quer reaberto. */}
                {aberta && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    {podeAssumir ? (
                      <span
                        className="blue-button btn-atendido"
                        onClick={atendidoConfirmation.bind(
                          null,
                          ur.id || ur._id,
                          ur.cliente?.nome,
                        )}
                      >
                        Vou atender agora
                      </span>
                    ) : (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        sx={{ whiteSpace: "nowrap", pt: 0.5 }}
                      >
                        {ur.avisarMedicos?.length
                          ? "A aguardar um dos dentistas avisados"
                          : "A aguardar um dentista"}
                      </Typography>
                    )}

                    {/* Tirar da fila sem atender: o doente foi embora, ou foi
                        marcada por engano. Discreto de propósito — é a saída
                        de exceção, não a ação que se espera. */}
                    {aoDispensar && (
                      <Typography
                        variant="caption"
                        onClick={() =>
                          aoDispensar(ur.id || ur._id, ur.cliente?.nome)
                        }
                        sx={{
                          cursor: "pointer",
                          color: "text.secondary",
                          textDecoration: "underline",
                          whiteSpace: "nowrap",
                          "&:hover": { color: "error.main" },
                        }}
                      >
                        Dispensar
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Modal>
  );
}

export default UrgenciasModal;
